/**
 * The vocabulary shared by every layer.
 *
 * Two rules keep this file honest:
 *   1. Nothing here imports the DOM. `core/` runs unchanged in Node so the
 *      detector can be evaluated headlessly.
 *   2. `SignalFrame` is simultaneously the runtime type AND the on-disk
 *      recording format. If those ever diverge, replay stops being trustworthy.
 */

// ---------------------------------------------------------------------------
// Signal
// ---------------------------------------------------------------------------

export interface Pose {
  /** Radians. Positive = looking up. */
  pitch: number;
  /** Radians. Positive = turned to the subject's left. */
  yaw: number;
  /** Radians. Positive = head tilted to the subject's right. */
  roll: number;
}

export interface EyePair<T> {
  left: T;
  right: T;
}

/**
 * One processed camera frame, reduced to signals. Never contains pixels — which
 * is exactly why recordings of these are safe to commit to a public repo.
 */
export interface SignalFrame {
  /** Milliseconds since the session started. Derived from the video frame's own
   *  presentation timestamp, not from wall-clock at handler time. */
  t: number;
  faceFound: boolean;
  /** Eye Aspect Ratio, computed in pixel space. NaN when no face. */
  ear: EyePair<number>;
  /** ARKit `eyeBlinkLeft` / `eyeBlinkRight`, 0..1. */
  blink: EyePair<number>;
  /** ARKit `eyeSquintLeft` / `eyeSquintRight`, 0..1. */
  squint: EyePair<number>;
  pose: Pose;
  /** Interpupillary distance in pixels — the crude screen-distance proxy. */
  ipd: number;
  /**
   * Set when the frame should not be trusted for blink decisions: extreme yaw
   * (far eye is foreshortened) or a large pose delta (spec §9 false-positive
   * risk during fast head movement).
   */
  lowConfidence: boolean;
  /**
   * Eye + iris landmark positions in pixel space, flat [x0,y0,x1,y1,...].
   * Recorded so that changing the EAR formula or the landmark index tables
   * later still replays against fixtures captured today. ~2.5 KB/s at 15fps.
   */
  eyeLandmarks: number[];
  /** All 52 blendshapes in ARKit order. Same rationale as eyeLandmarks. */
  blendshapes: number[];
}

// ---------------------------------------------------------------------------
// Events — the contract the game layer subscribes to (spec §4)
// ---------------------------------------------------------------------------

export type BlinkKind = "complete" | "partial";
export type Eye = "left" | "right" | "both";

export interface BlinkEvent {
  type: "blink";
  /** Frame time at which the blink was *decided*. */
  t: number;
  /** Frame time at which the closing phase began. */
  startedAt: number;
  /** Milliseconds from closing onset to reopening. */
  duration: number;
  /** How far the signal fell, normalised 0..1 against the open baseline. */
  amplitude: number;
  kind: BlinkKind;
  eye: Eye;
  /**
   * `t` minus the frame time of the deepest sample. The cost of smoothing plus
   * hysteresis, in ms. This is the number that decides whether blink-as-a-button
   * feels responsive, so it travels with every event rather than being inferred.
   */
  latency: number;
}

export interface NoBlinkAlert {
  type: "no-blink";
  t: number;
  /** Milliseconds since the last complete blink. */
  elapsed: number;
}

export interface FaceLostEvent {
  type: "face-lost";
  t: number;
}

export interface FaceFoundEvent {
  type: "face-found";
  t: number;
}

/**
 * Continuous suppression pressure (game design doc §6).
 *
 * Emitted on a fixed tick rather than per frame, so mechanics feel the same
 * whether the camera manages 15fps or 30.
 */
export interface SuppressionLevel {
  type: "suppression";
  t: number;
  /** 0 = healthy natural rhythm, 1 = sustained suppression, maximum penalty. */
  value: number;
  msSinceLastBlink: number;
  rollingRatePerMin: number;
  /** Complete / total blinks in the window. Low means habitual half-blinking. */
  completenessRatio: number;
}

/**
 * A thumb press. The moment-to-moment verb (design doc §2, §3).
 *
 * Blinking cannot be the high-frequency input: a healthy rate is one every 3–4
 * seconds, so any mechanic needing them faster fights physiology and trains the
 * wrong pattern. Blink is the low-frequency, high-impact verb; this is the
 * other one.
 */
export interface PointerInputEvent {
  type: "pointer";
  t: number;
  /**
   * `move` is emitted only while a press is held — a drag, never a hover.
   *
   * It exists because a steering control needs the gesture, not the landing
   * point: where the thumb travelled from is the input. Gating on held means
   * the event volume is proportional to actual dragging rather than to mouse
   * movement, so a session that never drags records nothing extra.
   */
  phase: "down" | "up" | "move";
  /** Normalised 0..1 within the play surface, so replay is resolution-agnostic. */
  x: number;
  y: number;
}

export interface SquintEvent {
  type: "squint";
  t: number;
  /** Milliseconds the squint has been sustained. */
  duration: number;
  intensity: number;
}

export type DetectorEvent =
  | BlinkEvent
  | NoBlinkAlert
  | FaceLostEvent
  | FaceFoundEvent
  | SquintEvent
  | SuppressionLevel
  | PointerInputEvent;

export type DetectorEventType = DetectorEvent["type"];

// ---------------------------------------------------------------------------
// Recording
// ---------------------------------------------------------------------------

/** How a ground-truth label was produced. Determines how much to trust its timing. */
export type LabelSource = "keytap" | "metronome" | "manual";

export interface GroundTruthLabel {
  t: number;
  kind: "blink";
  source: LabelSource;
}

export interface RecordingMeta {
  /**
   * Capture rate the session ACTUALLY achieved, measured from frame timestamps.
   *
   * This used to store the configured target, which made every fixture claim a
   * frame rate it never reached: the first real recording said 24fps and had
   * run at 14.9. Since capture rate drives amplitude accuracy and therefore the
   * complete/partial split, a fixture that misreports it is worse than one with
   * no metadata at all.
   */
  fps: number;
  /** What was asked for, kept so the gap between intent and reality is visible. */
  targetFps?: number;
  resolution: string;
  /** Free-form capture conditions. These are what make the fixture set useful:
   *  "glasses", "backlit", "low light", "phone front camera". */
  conditions: string[];
  notes?: string;
  recordedAt: string;
  userAgent: string;
  /**
   * Size actually fed to the model, e.g. "270x480". Without it a fixture cannot
   * be attributed to a build: six sessions all reported 14.9fps and there was no
   * way to tell which of them had inference downscaling active.
   */
  inferenceSize?: string;
  /** Mean cost of one inference call. The real ceiling on capture rate. */
  inferenceMs?: number;
  /**
   * Rate the camera delivered frames at, before throttling. Recorded because
   * without it "the hardware is slow" and "we are dropping frames" look
   * identical in a fixture — and for nine sessions they were.
   */
  sourceFps?: number;
  /**
   * Detector settings in force during capture. These do NOT affect the stored
   * signal — the recording holds raw values and every threshold is applied at
   * eval time — but they explain what the person watching the screen saw, which
   * matters when they report that something looked wrong.
   */
  detector?: Pick<DetectorConfig, "signal" | "closeThreshold" | "completeAmplitude"> & {
    filter: FilterKind;
  };
  /**
   * What was being played, and what a tap meant, when recording STARTED.
   *
   * The starting state only. `Recording.modeChanges` carries the rest — see
   * the note there for why a snapshot alone turned out not to be enough.
   *
   * Plain strings rather than the game layer's own union: `core/` does not
   * import from `game/`, and this file is also compiled under the Functions
   * tsconfig, which cannot see the browser sources at all.
   *
   * Worth storing because it is the difference between "the detector missed
   * everything" and "taps were shooting, not labelling". A play session has no
   * labels by construction, and without this field that is indistinguishable
   * months later from a session where labelling simply failed.
   */
  mode?: string;
  mechanic?: string;
}

export interface Recording {
  version: 1;
  meta: RecordingMeta;
  frames: SignalFrame[];
  labels: GroundTruthLabel[];
  /**
   * Thumb input captured during the session. Optional so every fixture recorded
   * before the game layer existed still parses.
   *
   * Stored alongside the signal for the same reason the signal is stored at all:
   * a play session should replay completely, input and eyes together, or tuning
   * game feel means going back to the device every time.
   */
  inputs?: PointerInputEvent[];
  /**
   * Every time the mode or the mechanic changed, with the starting state first.
   *
   * `meta.mode` and `meta.mechanic` snapshot what the session STARTED as, which
   * turned out not to be enough. A real session labelled three blinks, switched
   * to a mechanic at fourteen seconds, and spent the remaining forty-four
   * seconds steering — and filed itself as `debug`/`none` with three labels,
   * which is indistinguishable from a session where labelling silently broke.
   * It cost an investigation to establish that nothing was broken at all.
   *
   * A session has to be able to explain its own shape. This is that.
   */
  modeChanges?: ModeChange[];
}

/** A point where a tap stopped meaning one thing and started meaning another. */
export interface ModeChange {
  /** Frame time, the same clock as the signal and the labels. */
  t: number;
  mode: string;
  mechanic: string;
}

// ---------------------------------------------------------------------------
// Detector configuration
// ---------------------------------------------------------------------------

export type SignalKind = "ear" | "blendshape";

export type FilterKind = "none" | "moving-average" | "median" | "one-euro";

export interface FilterConfig {
  kind: FilterKind;
  /** Window size for moving-average and median. */
  window: number;
  /** One-Euro minimum cutoff frequency, Hz. Lower = smoother, more lag. */
  minCutoff: number;
  /** One-Euro speed coefficient. Higher = less lag during fast movement. */
  beta: number;
}

export interface DetectorConfig {
  signal: SignalKind;
  filter: FilterConfig;
  /**
   * Hysteresis, expressed on the *normalised* closure signal where 0 = fully
   * open baseline and 1 = fully closed. Two thresholds, not one, so the state
   * machine cannot chatter around a single boundary (spec §2).
   */
  closeThreshold: number;
  openThreshold: number;
  /** Below this normalised depth a closure is reported as partial, not complete. */
  completeAmplitude: number;
  /** Closures shorter than this are noise, not blinks. */
  minDurationMs: number;
  /** Closures longer than this are the subject resting their eyes, not blinking. */
  maxDurationMs: number;
  /** Emit NoBlinkAlert after this long without a complete blink. */
  noBlinkAfterMs: number;
  /**
   * Openness at full closure, as a fraction of the open baseline. Overrides the
   * per-signal prior in core/closure.ts.
   *
   * Exposed because the prior turned out to be badly wrong on real faces. It
   * assumes a shut eye reads ~0 openness on the blendshape; measured on a phone
   * held at lap height it reads ~0.2, because looking down lowers the eyelids
   * and MediaPipe scores that as partially blinked at rest. Getting this wrong
   * compresses the whole normalised range and misplaces every threshold at once.
   */
  closedFraction?: number;
  /** Frames beyond this yaw are marked low-confidence. Radians. */
  maxYaw: number;
  /** Pose change per frame beyond this marks low-confidence. Radians. */
  maxPoseDelta: number;
}

export const DEFAULT_CONFIG: DetectorConfig = {
  signal: "blendshape",
  // beta is high on purpose. A blink at 15fps is 3–4 samples, so a filter tuned
  // for gentle smoothing flattens the peak below the detection threshold before
  // the state machine ever sees it — shallow blinks vanish entirely. A large
  // speed coefficient means the cutoff opens up during the closure edge and the
  // amplitude survives, while the open-eye baseline still gets smoothed hard.
  filter: { kind: "one-euro", window: 3, minCutoff: 1.0, beta: 2.0 },
  // Spec §2 suggests 0.55/0.35. Entering the closure state that late leaves
  // almost no band beneath completeAmplitude, so a partial blink is simply never
  // seen — and partial detection is the whole basis of the mechanic in §8 that
  // teaches people to blink properly. Entering earlier makes the partial band
  // reachable; `npm run eval -- --sweep closeThreshold=0.3:0.6:0.05` is how the
  // real number gets chosen once there are fixtures to choose it from.
  closeThreshold: 0.4,
  openThreshold: 0.25,
  completeAmplitude: 0.7,
  minDurationMs: 40,
  maxDurationMs: 600,
  noBlinkAfterMs: 8000,
  maxYaw: 0.44, // ~25°
  // ~14° between consecutive frames. The obvious value here is much tighter,
  // and much tighter is wrong: a phone held in one hand moves relative to the
  // face on every single frame, so a 5° gate marks nearly everything untrusted
  // and the detector goes silent on the device people actually use. A real head
  // turn is both larger and sustained, so it still trips this.
  maxPoseDelta: 0.25,
};

/**
 * Calibration output (spec §3). Absent until the user runs the flow; the
 * detector falls back to a rolling baseline so it is usable before then.
 */
export interface Calibration {
  earOpen: EyePair<number>;
  earClosed: EyePair<number>;
  blinkOpen: EyePair<number>;
  blinkClosed: EyePair<number>;
  /** Reference IPD in px, for the screen-distance proxy. */
  ipdReference: number;
  /** Median blink duration observed during calibration, ms. */
  typicalDurationMs: number;
  capturedAt: string;
}
